import React, { useEffect, useState } from 'react';
import { Close, Delete } from "@mui/icons-material";
import api from '../../services/api';

interface EspecieFormProps {
  /** uid da espécie sendo editada — ausente = criando uma nova */
  uidEditando?: string;
  onClose: () => void;
  onSaved: () => void;
}

const TIPOS_NOME: { value: string; label: string }[] = [
  { value: 'sinonimo', label: 'Sinônimo (vale em qualquer projeto/região)' },
  { value: 'regional', label: 'Nome regional (vale só numa área específica)' },
];

const EspecieForm: React.FC<EspecieFormProps> = ({ uidEditando, onClose, onSaved }) => {
  const isEdicao = !!uidEditando;
  const [carregando, setCarregando] = useState(isEdicao);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const [nomePopular, setNomePopular] = useState('');
  const [nomeCientifico, setNomeCientifico] = useState('');

  const [nomes, setNomes] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [novoNome, setNovoNome] = useState('');
  const [novoTipo, setNovoTipo] = useState<'sinonimo' | 'regional'>('sinonimo');
  const [novaArea, setNovaArea] = useState('');
  const [salvandoNome, setSalvandoNome] = useState(false);

  useEffect(() => {
    api.get('/pesca/areas').then((res) => setAreas(res.data.data)).catch(() => setAreas([]));
  }, []);

  useEffect(() => {
    if (!uidEditando) return;
    api.get(`/pesca/especies/${uidEditando}`).then((res) => {
      const d = res.data.data;
      setNomePopular(d.nome_popular || '');
      setNomeCientifico(d.nome_cientifico || '');
      // O nome popular canônico já aparece no campo acima — não repete na
      // lista de aliases/nomes regionais pra não parecer duplicado.
      setNomes((d.nomes || []).filter((n: any) => !(n.tipo === 'popular' && !n.uid_pesca_area)));
      setCarregando(false);
    }).catch(() => {
      setErro('Não foi possível carregar essa espécie.');
      setCarregando(false);
    });
  }, [uidEditando]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nomePopular.trim()) {
      setErro('Informe o nome popular.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = { nome_popular: nomePopular.trim(), nome_cientifico: nomeCientifico.trim() || null };
      if (isEdicao) {
        await api.put(`/pesca/especies/${uidEditando}`, payload);
      } else {
        await api.post('/pesca/especies', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar espécie.');
    } finally {
      setSalvando(false);
    }
  };

  const handleAdicionarNome = async () => {
    if (!novoNome.trim() || !uidEditando) return;
    if (novoTipo === 'regional' && !novaArea) {
      setErro('Escolha a área do nome regional.');
      return;
    }
    setSalvandoNome(true);
    setErro('');
    try {
      const res = await api.post(`/pesca/especies/${uidEditando}/nomes`, {
        tipo: novoTipo,
        nome: novoNome.trim(),
        uid_pesca_area: novoTipo === 'regional' ? novaArea : null,
      });
      setNomes((prev) => [...prev, res.data.data]);
      setNovoNome('');
      setNovaArea('');
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao adicionar nome/alias.');
    } finally {
      setSalvandoNome(false);
    }
  };

  const handleRemoverNome = async (uidNome: string) => {
    try {
      await api.delete(`/pesca/especies/nomes/${uidNome}`);
      setNomes((prev) => prev.filter((n) => n.uid_pesca_especie_nome !== uidNome));
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao remover nome/alias.');
    }
  };

  const nomeDaArea = (uidArea: string) => areas.find((a) => a.uid_pesca_area === uidArea)?.nome || uidArea;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)', fontSize: '1.2rem' }}>
            {isEdicao ? 'Editar Espécie' : 'Nova Espécie'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        {carregando ? (
          <p>Carregando...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Nome Popular *</label>
              <input style={inputStyle} value={nomePopular} onChange={(e) => setNomePopular(e.target.value)} placeholder="Ex: Mandi" />
            </div>
            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Nome Científico</label>
              <input style={inputStyle} value={nomeCientifico} onChange={(e) => setNomeCientifico(e.target.value)} placeholder="Ex: Pimelodus maculatus" />
            </div>

            {isEdicao && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '8px' }}>
                <label style={{ ...labelStyle, fontWeight: 'bold', color: '#444' }}>
                  Aliases e nomes regionais
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '8px 0' }}>
                  {nomes.length === 0 && <p style={{ color: '#999', fontSize: '0.82rem', margin: 0 }}>Nenhum alias/nome regional cadastrado.</p>}
                  {nomes.map((n) => (
                    <div key={n.uid_pesca_especie_nome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7f7f7', padding: '4px 8px', borderRadius: '6px' }}>
                      <span style={{ fontSize: '0.85rem' }}>
                        {n.nome}
                        <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#888' }}>
                          {n.uid_pesca_area ? `regional — ${nomeDaArea(n.uid_pesca_area)}` : 'sinônimo'}
                        </span>
                      </span>
                      <Delete fontSize="small" style={{ color: '#f75a68', cursor: 'pointer' }} onClick={() => handleRemoverNome(n.uid_pesca_especie_nome)} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    style={{ ...inputStyle, flex: 2, minWidth: '140px' }}
                    value={novoNome}
                    onChange={(e) => setNovoNome(e.target.value)}
                    placeholder="Novo nome/alias (ex: bagre-amarelo)"
                  />
                  <select style={{ ...inputStyle, flex: 1, minWidth: '160px' }} value={novoTipo} onChange={(e) => setNovoTipo(e.target.value as any)}>
                    {TIPOS_NOME.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {novoTipo === 'regional' && (
                    <select style={{ ...inputStyle, flex: 1, minWidth: '140px' }} value={novaArea} onChange={(e) => setNovaArea(e.target.value)}>
                      <option value="">Escolha a área...</option>
                      {areas.map((a) => <option key={a.uid_pesca_area} value={a.uid_pesca_area}>{a.nome}</option>)}
                    </select>
                  )}
                  <button type="button" onClick={handleAdicionarNome} disabled={salvandoNome || !novoNome.trim()} style={secondaryBtnStyle}>
                    Adicionar
                  </button>
                </div>
              </div>
            )}

            {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
              <button type="submit" disabled={salvando} style={primaryBtnStyle}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};

export default EspecieForm;
